import { useCallback, useEffect, useState } from 'react'
import { api } from '../../lib/api'

/**
 * Operator session for the admin / customer-relations console.
 *
 * Deliberately separate from `ChooseChow`'s AuthContext. That context models a
 * customer↔chef app — role drives the dock, the tab bar, and which shell the
 * router mounts — and widening it to carry an operator would put an
 * operator-shaped hole through every one of those. The console instead lives on
 * its own route tree with its own session record.
 *
 * It does share the `auth_token` key, because that is what `lib/api.ts` stamps
 * on every request; signing into the console therefore replaces whatever app
 * session was in this browser, which is the honest behaviour — one bearer token
 * per browser, and it is an operator's.
 */

const TOKEN_KEY = 'auth_token'
const SESSION_KEY = 'ops_session'

/** Roles the server issues that this console accepts. */
const OPERATOR_ROLES = ['ops', 'support', 'admin'] as const
export type OperatorRole = (typeof OPERATOR_ROLES)[number]

export interface OpsUser {
  id: string
  profileId?: string
  displayName: string
  email: string
  role: OperatorRole
  organizationId?: string
}

interface SessionResponse {
  user: {
    id: string
    profile_id?: string
    display_name: string
    email: string
    role: string
    organization_id?: string
  }
  access_token: string
}

const isOperatorRole = (role: string): role is OperatorRole =>
  (OPERATOR_ROLES as readonly string[]).includes(role)

const readStored = (): OpsUser | null => {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as OpsUser
    // A stored session whose role is no longer an operator role is not a
    // session this console will honour.
    return parsed && isOperatorRole(parsed.role) ? parsed : null
  } catch {
    return null
  }
}

export interface OpsSession {
  user: OpsUser | null
  /** True until the stored session has been read once. */
  loading: boolean
  signIn: (email: string, password: string, role: OperatorRole) => Promise<OpsUser>
  signOut: () => void
}

export const useOpsSession = (): OpsSession => {
  const [user, setUser] = useState<OpsUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const sync = () => {
      const stored = readStored()
      const token = localStorage.getItem(TOKEN_KEY)
      setUser(stored && token ? stored : null)
      setLoading(false)
    }
    sync()
    window.addEventListener('chow-session-change', sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener('chow-session-change', sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string, role: OperatorRole) => {
    const resp = await api.post<SessionResponse>('/v1/user/authenticate', {
      email: email.trim().toLowerCase(),
      password,
      role,
    })
    if (!isOperatorRole(resp.user.role)) {
      throw new Error('This account does not have operator access.')
    }
    const next: OpsUser = {
      id: resp.user.id,
      profileId: resp.user.profile_id,
      displayName: resp.user.display_name,
      email: resp.user.email,
      role: resp.user.role,
      organizationId: resp.user.organization_id,
    }
    localStorage.setItem(TOKEN_KEY, resp.access_token)
    localStorage.setItem(SESSION_KEY, JSON.stringify(next))
    localStorage.removeItem('chow_session')
    window.dispatchEvent(new Event('chow-session-change'))
    setUser(next)
    return next
  }, [])

  const signOut = useCallback(() => {
    localStorage.removeItem(SESSION_KEY)
    localStorage.removeItem(TOKEN_KEY)
    window.dispatchEvent(new Event('chow-session-change'))
    setUser(null)
  }, [])

  return { user, loading, signIn, signOut }
}
